import { getDefaultOutlookSpacesService, OutlookSpacesOwsClient } from 'owa-timestream-service';
import type {
    NewOutlookSpaceAsset,
    OutlookSpaceAsset,
    OutlookSpaceAssetId,
    OutlookSpaceId,
    OutlookSpacesFilter,
    PatchOutlookSpace,
    PatchOutlookSpaceAsset,
    ProjectId,
    SyncParameters,
} from 'owa-timestream-types';
import getProjectIdInfo from './utils/getProjectIdInfo';
import { getProjectService } from './serviceMap';

function getService(projectId: ProjectId) {
    const { tenant, mailbox } = getProjectIdInfo(projectId);
    if (tenant && mailbox) {
        return new OutlookSpacesOwsClient(`${mailbox}@${tenant}`);
    }

    const projectService = getProjectService(projectId);
    return projectService || getDefaultOutlookSpacesService();
}

function getOutlookSpaceId(projectId: ProjectId): OutlookSpaceId {
    return getProjectIdInfo(projectId).outlookSpaceId;
}

//
// Outlook Spaces (Collection)
//
export function getOutlookSpaces(spacesFilter?: OutlookSpacesFilter) {
    return getDefaultOutlookSpacesService().getOutlookSpaces(spacesFilter);
}

//
// Outlook Space (Entity)
//
export function getOutlookSpace(projectId: ProjectId) {
    return getService(projectId).getOutlookSpace(getOutlookSpaceId(projectId));
}

export function deleteOutlookSpace(projectId: ProjectId) {
    return getService(projectId).deleteOutlookSpace(getOutlookSpaceId(projectId));
}

export function patchOutlookSpace(projectId: ProjectId, changes: PatchOutlookSpace) {
    return getService(projectId).patchOutlookSpace(getOutlookSpaceId(projectId), changes);
}

//
// Outlook Space Assets (Collection)
//
export function getOutlookSpaceAssets(projectId: ProjectId) {
    return getService(projectId).getOutlookSpaceAssets(getOutlookSpaceId(projectId));
}

export function canSyncAssets(projectId: ProjectId) {
    return getService(projectId).canSyncAssets();
}

export function syncAssets(projectId: ProjectId, syncParameters: SyncParameters) {
    return getService(projectId).syncAssets(getOutlookSpaceId(projectId), syncParameters);
}

export function createOutlookSpaceAsset(projectId: ProjectId, newAsset: NewOutlookSpaceAsset) {
    return getService(projectId).createOutlookSpaceAsset(
        getOutlookSpaceId(projectId),
        roundIntegers(newAsset)
    );
}

//
// Outlook Space Asset (Entity)
//
export function deleteOutlookSpaceAsset(projectId: ProjectId, assetId: OutlookSpaceAssetId) {
    return getService(projectId).deleteOutlookSpaceAsset(getOutlookSpaceId(projectId), assetId);
}

export function patchOutlookSpaceAsset(
    projectId: ProjectId,
    assetId: OutlookSpaceAssetId,
    changes: PatchOutlookSpaceAsset,
    returnFullObject: true
): Promise<OutlookSpaceAsset>;

export function patchOutlookSpaceAsset(
    projectId: ProjectId,
    assetId: OutlookSpaceAssetId,
    changes: PatchOutlookSpaceAsset,
    returnFullObject: false
): Promise<void>;

export function patchOutlookSpaceAsset(
    projectId: ProjectId,
    assetId: OutlookSpaceAssetId,
    changes: PatchOutlookSpaceAsset
): Promise<{ lastModifiedDateTime: string }>;

export function patchOutlookSpaceAsset(
    projectId: ProjectId,
    assetId: OutlookSpaceAssetId,
    changes: PatchOutlookSpaceAsset,
    returnFullObject?: boolean
) {
    return getService(projectId).patchOutlookSpaceAsset(
        getOutlookSpaceId(projectId),
        assetId,
        roundIntegers(changes),
        returnFullObject
    );
}

//
// Asset Reorder Children
//
export function reorderOutlookSpaceAssetChildren(
    projectId: ProjectId,
    assetId: OutlookSpaceAssetId
) {
    return getService(projectId).reorderOutlookSpaceAssetChildren(
        getOutlookSpaceId(projectId),
        assetId
    );
}

//
// Recycle Bin
//
export function emptyOutlookSpaceRecycleBin(projectId: ProjectId) {
    return getService(projectId).emptyOutlookSpaceRecycleBin(getOutlookSpaceId(projectId));
}

function roundIntegers<T extends PatchOutlookSpaceAsset>(p: T): T {
    p.x = roundInteger(p.x);
    p.y = roundInteger(p.y);
    p.width = roundInteger(p.width);
    p.height = roundInteger(p.height);
    p.rotation = roundInteger(p.rotation);
    return p;
}

function roundInteger(value: number | undefined): number | undefined {
    return !!value ? Math.round(value) : value;
}
